/**
 * Funnel templates — small presets that decide how many suggested-page rows
 * show up in the editor nav for a newly created funnel.
 *
 * Designed to be extended: today we ship `empty` + `basic`, but the shape
 * (id + label + description + minRows + preview) is intentionally generic so
 * a full template system (with prepopulated product slots, routing presets,
 * etc.) can slot in without reshaping the type.
 */

export type FunnelTemplateId = 'empty' | 'basic';

export interface FunnelTemplatePreviewRow {
    title: string;
    subtitle?: string;
}

export interface FunnelTemplateDefinition {
    id: FunnelTemplateId;
    label: string;
    description: string;
    /**
     * How many rows the nav should surface initially. Saved pages + the auto-
     * created draft count toward this — suggested placeholders pad the rest.
     */
    minRows: number;
    /** Rows rendered inside the template card preview visualization. */
    preview: FunnelTemplatePreviewRow[];
}

export const FUNNEL_TEMPLATES: readonly FunnelTemplateDefinition[] = [
    {
        id: 'empty',
        label: 'Empty',
        description: 'Start with a single entry page and build from scratch.',
        minRows: 1,
        preview: [{ title: 'Entry Page', subtitle: 'Initial Lead Magnet or Paid Product' }],
    },
    {
        id: 'basic',
        label: 'Basic',
        description: 'An entry page, a subsequent offer and a following offer, the classic sequence.',
        minRows: 3,
        preview: [
            { title: 'Entry Page', subtitle: 'Initial Lead Magnet or Paid Product' },
            { title: 'Subsequent Offer', subtitle: '' },
            { title: 'Following Offer', subtitle: '' },
        ],
    },
] as const;

export const DEFAULT_FUNNEL_TEMPLATE_ID: FunnelTemplateId = 'basic';

export function getFunnelTemplate(id: FunnelTemplateId | null | undefined): FunnelTemplateDefinition {
    return FUNNEL_TEMPLATES.find(t => t.id === id) ?? FUNNEL_TEMPLATES.find(t => t.id === DEFAULT_FUNNEL_TEMPLATE_ID)!;
}

// ----------------------------------------------------------------------------
// Per-funnel template persistence (localStorage).
// Captured at create-time, consumed by the editor to seed `minRows`.
// TODO: Will be replaced by a server-backed field once the template system matures.
// ----------------------------------------------------------------------------

const STORAGE_KEY_PREFIX = 'funnel-template:';

export function persistFunnelTemplate(storeId: number, templateId: FunnelTemplateId) {
    try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${storeId}`, templateId);
    } catch {
        // no-op — falling back to default is fine
    }
}

export function readFunnelTemplate(storeId: number | undefined): FunnelTemplateId | null {
    if (!storeId) return null;
    try {
        const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${storeId}`);
        if (raw === 'empty' || raw === 'basic') return raw;
    } catch {
        // no-op
    }
    return null;
}
